import { DomHelper } from './DomHelper';

/**
 * ScrollAnimation — 汎用スクロールアニメーション（全ページ共通）
 *
 * 使い方:
 *   HTML: class="js-scroll-animation" + 以下の属性（任意）
 *   JS:   ScrollAnimation.initAll() — main.ts の initCommon() から自動実行
 *   CSS:  foundation/_scroll-animation.scss
 *
 * 属性一覧（すべて任意）:
 *   data-anim="fade-up|slide-left|slide-right|scale-up|blur-in"
 *     アニメーション種別。省略時は translateY フェードアップ。
 *
 *   data-anim-offset="100"
 *     ビューポート下端からさらに X px スクロールして初めて発火（px）。
 *     同じ offset 値を持つ要素は同一 Observer を共有。
 *
 *   data-anim-delay="300"
 *     交差後 X ms 遅れて .is-visible を付与（ms）。
 *
 * 親要素属性:
 *   data-anim-stagger="120"
 *     同一親内の兄弟要素が同時交差した場合のスタガー間隔（ms）。
 *     省略時は 80ms。
 */
// biome-ignore lint/complexity/noStaticOnlyClass: static class used as module namespace
export class ScrollAnimation {
  /** element → その要素を監視している Observer のマップ（reset 用） */
  private static elementObserverMap = new Map<HTMLElement, IntersectionObserver>();

  /**
   * 全ページ共通の初期化
   * initCommon() から呼び出す（各ページクラスでの個別呼び出し不要）
   */
  static initAll(): void {
    const targets = DomHelper.qsa<HTMLElement>('.js-scroll-animation');
    if (!targets.length) return;

    // text-letter / text-rotate: 交差前に文字を span で分割（DOM 操作は1回のみ）
    targets.forEach((el) => ScrollAnimation.splitCharsForTextAnim(el));

    // data-anim-offset の値でグルーピング → 同じ offset は Observer を共有
    const byOffset = new Map<number, HTMLElement[]>();
    targets.forEach((el) => {
      const offset = Number.parseInt(el.dataset.animOffset ?? '0', 10) || 0;
      if (!byOffset.has(offset)) byOffset.set(offset, []);
      byOffset.get(offset)?.push(el);
    });

    byOffset.forEach((els, offset) => {
      const obs = new IntersectionObserver(
        (entries) => {
          // 交差した要素を親要素でグルーピング（スタガー計算のため）
          const byParent = new Map<Element | null, HTMLElement[]>();

          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target as HTMLElement;
            obs.unobserve(el);

            const parent = el.parentElement;
            if (!byParent.has(parent)) byParent.set(parent, []);
            byParent.get(parent)?.push(el);
          });

          byParent.forEach((groupEls) => {
            // DOM 順にソートしてスタガー順を保証
            groupEls.sort((a, b) =>
              a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
            );

            groupEls.forEach((el, i) => {
              // 個別 delay（data-anim-delay）
              const ownDelay = Number.parseInt(el.dataset.animDelay ?? '0', 10) || 0;

              // 親要素の data-anim-stagger でスタガー間隔を取得（デフォルト 80ms）
              const staggerMs =
                Number.parseInt(el.parentElement?.dataset.animStagger ?? '80', 10) || 80;

              // 兄弟が複数同時交差した場合のみスタガーを適用
              const staggerDelay = groupEls.length > 1 ? i * staggerMs : 0;

              setTimeout(() => DomHelper.addClass(el, 'is-visible'), ownDelay + staggerDelay);
            });
          });
        },
        {
          // offset 分だけビューポート下端を内側に縮めることで「余分にスクロールして発火」を実現
          rootMargin: `0px 0px -${offset}px 0px`,
          threshold: 0.1,
        }
      );

      els.forEach((el) => {
        obs.observe(el);
        ScrollAnimation.elementObserverMap.set(el, obs);
      });
    });
  }

  /**
   * text-letter / text-rotate / text-wave / text-blur / text-clip:
   * テキストを .js-scroll-char span に分割し、transition-delay でスタガーを付与する。
   * text-clip のみ overflow:hidden の .js-scroll-char-wrap をダブルラッパーとして生成する。
   * @param el 対象要素
   */
  private static splitCharsForTextAnim(el: HTMLElement): void {
    const anim = el.dataset.anim;
    const charSplitAnims = ['text-letter', 'text-rotate', 'text-wave', 'text-blur', 'text-clip'];
    if (!charSplitAnims.includes(anim ?? '')) return;
    if (el.querySelector('.js-scroll-char')) return; // 既に分割済み

    const text = (el.textContent ?? '').trim();

    const useWrap = anim === 'text-clip'; // text-clip はマスク用ダブルラッパーが必要

    // 1文字 span は装飾用のため個別に aria-hidden。
    // スクリーンリーダー用には visually-hidden の全文を先頭に置く。
    // （span は暗黙ロール generic で aria-label が禁止属性のため使用しない）
    const chars = [...text]
      .map((char, i) => {
        const safeChar = char === ' ' ? '\u00A0' : char;
        const inner = `<span class="js-scroll-char" style="transition-delay:${i * 80}ms" aria-hidden="true">${safeChar}</span>`;
        return useWrap
          ? `<span class="js-scroll-char-wrap" aria-hidden="true">${inner}</span>`
          : inner;
      })
      .join('');

    el.innerHTML = `<span class="u-visually-hidden">${text}</span>${chars}`;
  }

  /**
   * 指定セレクターのアニメーションをリセットして再観察
   * デモページのリセットボタン等から使用
   * @param selector 対象セレクター（デフォルト: .js-scroll-animation）
   */
  static reset(selector = '.js-scroll-animation'): void {
    const targets = DomHelper.qsa<HTMLElement>(selector);
    targets.forEach((el) => {
      DomHelper.removeClass(el, 'is-visible');
      setTimeout(() => {
        const obs = ScrollAnimation.elementObserverMap.get(el);
        obs?.observe(el);
      }, 50);
    });
  }
}
